import React, { useRef } from 'react';
import dayjs from 'dayjs';
import { useTable, useGlobalFilter, usePagination, useFilters, useSortBy } from 'react-table'
import SelectColumnFilter from '../ReactTable/SelectColumnFilter';
import { useHistory } from 'react-router';
import { COLORS } from '../../config';
import SecondaryButton from '../../components/Buttons/SecondaryButton';
import SecondaryButtonAccent from '../../components/Buttons/SecondaryButtonAccent';
import PlusIcon from '../../components/Icons/solid/PlusIcon';
import AdjustmentsIcon from '../../components/Icons/solid/AdjustmentsIcon';
import DotsIcon from '../../components/Icons/solid/DotsIcon';
import GlobalFilter from '../ReactTable/GlobalFilter';
import TablePagination from '../ReactTable/TablePagination';
import FiltersModal from '../ReactTable/FiltersModal';
import SortAscSmall from '../../components/Icons/solid/SortAscSmall';
import SortDescSmall from '../../components/Icons/solid/SortDescSmall';
import MoonLoader from 'react-spinners/MoonLoader'
import TableRowSubMenu from '../../components/Menus/TableRowSubMenu';
import useScans from '../../store/scans/scansContext';
import DocSearchItem from '../../components/Icons/outline/DocSearchIcon';
import DeleteScanModal from './DeleteScanModal';
import useApp from '../../store/app/appContext';
import { TrashIcon } from '@heroicons/react/outline';
import useOnClickOutside from '../../hooks/useOnClickOutside';

function PatientScans({ patientId }) {

  const ref = useRef();
  
  const {
    appState: { userAccount },
  } = useApp();

  const {  getPatientScans, deleteScan } = useScans();
  
  const history = useHistory();

  const [isLoading, setIsLoading] = React.useState(true);
  const [showDeleteScanModal, setShowDeleteScanModal] = React.useState(false);
  const [scanToDelete, setScanToDelete] = React.useState(null);
  const [patientScans, setPatientScans] = React.useState([]);
  const [showSubMenu, setShowSubMenu] = React.useState({
    active: false,
    row: null
  })

  React.useEffect(() => {
    if(patientId){
      getPatientScans(patientId).then((res) => {
        const scans = res;
        setIsLoading(false)
        setPatientScans(scans);
      });
    }
  }, [patientId])

  useOnClickOutside(
    ref,
    setShowSubMenu.bind(this, {
      active: false,
      row: null,
    })
  );

  const [showFilters, setShowFilters] = React.useState(false)

  const data = React.useMemo(
    () => []
  
  , [patientScans]);

  if (patientScans && patientScans.length && !data.length) {
    patientScans.forEach(scan => {
      data.push({
        id: scan._id,
        fullName: scan?.user_id?.first_name + ' ' + scan?.user_id?.last_name || '',
        uploaded: dayjs(scan.created_at).format('DD/MM/YYYY'),
        type: scan.type,
        description: scan.description
      })
    })
  }

  const handleRowSubMenuClick = (row) => {
    setShowSubMenu({
      active: !showSubMenu.active,
      row: row
    })
  }

  const handleViewDetails = (rowIndex) => {
    if (userAccount.isAdmin || userAccount.isPartner) history.push(`/manage-patients/${patientId}/scan-details/${patientScans[rowIndex]._id}`)
    if (userAccount.isPatient) history.push(`/my-scans/${patientId}/scan-details/${patientScans[rowIndex]._id}`)
    setShowSubMenu({
      active: false,
      row: null
    })
  }

  const addScan = () =>{
    if(userAccount.isPatient) history.push(`/my-scans/${patientId}/add-scan`)
    if(userAccount.isAdmin || userAccount.isPartner) history.push(`/manage-patients/${patientId}/add-scan`)
  }

  const defaultColumn = React.useMemo(() => {
    return {
      Filter: SelectColumnFilter
    }
  }, [])

  const handleRemoveScan = (scanInfo) => {
    setScanToDelete(scanInfo);
    setShowDeleteScanModal(true);
  }

  const handleDeleteScan = (scanInfo) => {
    deleteScan(scanInfo.id).then(res => {
      const updatedArray = patientScans.filter(scan => scan._id !== scanInfo.id);
      setPatientScans(updatedArray);
    })
    setShowSubMenu({
      active: false,
      row: null
    })
  }

  const columns = React.useMemo(
    () => [
      {
        Header: 'User',
        accessor: 'fullName',
        disableFilters: true,
      },
      {
        Header: 'Type',
        accessor: 'type',
        disableFilters: true,
      },
      {
        Header: 'Description',
        accessor: 'description',
      },
      {
        Header: 'Uploaded',
        accessor: 'uploaded',
      },
    ], []
  );

  const { 
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    gotoPage,
    prepareRow,
    state : { globalFilter, pageIndex, pageSize },
    preGlobalFilteredRows,
    setGlobalFilter,
    setAllFilters
  } = useTable(
    {
      columns,
      data,
      defaultColumn
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );


  return (
    <div className='w-full rounded bg-white shadow'>
      <FiltersModal
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        headerGroups={headerGroups}
        setAllFilters={setAllFilters}
      />
      <DeleteScanModal 
        showDeleteScanModal={showDeleteScanModal}
        setShowDeleteScanModal={setShowDeleteScanModal}
        onDelete={() => handleDeleteScan(scanToDelete)}
        forceDelete={scanToDelete?.deleted ? true : false}
      />
      <div className='p-2 xl:p-4 grid grid-cols-1 gap-y-2 lg:flex lg:items-center w-full'>
        <SecondaryButtonAccent text="Add scan" onClick={addScan} icon={<PlusIcon fill={COLORS.colorPrimary}/>}/>
        <SecondaryButton text="Set filters" onClick={() => setShowFilters(true)} className='lg:ml-auto lg:mr-2' icon={<AdjustmentsIcon fill={COLORS.colorSecondary}/>}/>
        <div>
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </div>
      </div>
      {isLoading && 
      <div className='flex justify-around min-h-170 pt-8'>
        <MoonLoader color={COLORS.colorPrimary}/>
      </div> 
      }
      {!isLoading &&
      <div className='overflow-x-scroll'>
        <table {...getTableProps()} className='w-full mt-4 border-8 border-transparent'>
          <thead >
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                <th className='text-left xl:px-2 pb-3 border-b-2 border-gray-100 '/>
                {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())} className='text-secondary xl:px-2 pb-3 border-b-2 border-gray-100 text-left text-base font-semibold'>
                  <span className='flex items-center'>
                  {column.render('Header')}
                  {column.isSorted ? 
                    column.isSortedDesc ? 
                      <SortAscSmall style={'ml-1 h-5 w-5'}/>
                      : <SortDescSmall style={'ml-1 h-5 w-5'} />
                  : ''}
                  </span>
                </th>
                ))}
                <th className='text-left xl:px-2 pb-3 border-b-2 border-gray-100 '></th>  
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()} className={`border-b-2 border-gray-100`}>
                  <td  className='text-secondary font-medium xl:px-2 py-3'/>
                  {row.cells.map(cell => {
                    return (
                      <td {...cell.getCellProps()} className='text-gray-500  xl:px-2 py-3'>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                  <td className='text-left xl:px-2 relative'>
                    <div ref={ref} className='cursor-pointer flex flex-row-reverse '>
                      <DotsIcon vertical fill={COLORS.colorShade} onClick={() => handleRowSubMenuClick(row.index)}/>
                    </div>
                    {showSubMenu.active && showSubMenu.row === row.index &&
                    <TableRowSubMenu>
                      <ul className='whitespace-nowrap'>
                        <li className='cursor-pointer flex items-center' onClick={() => handleViewDetails(row.index)}>
                          <DocSearchItem size='w-5 h-5' stroke={COLORS.colorPrimary}/>
                          <span className='ml-3'>View scan details</span>
                        </li>
                        {!userAccount.isPatientOrPartner && <li className='cursor-pointer flex items-center mt-2' onClick={() => handleRemoveScan(row.original)}>
                          <TrashIcon className='w-5 h-5' stroke={COLORS.colorPrimary}/>
                          <span className='ml-3'>{'Delete Scan'}</span>
                        </li>}
                      </ul>
                    </TableRowSubMenu>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      }
      <TablePagination   
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        previousPage={previousPage}
        nextPage={nextPage}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        setPageSize={setPageSize}
        pageSize={pageSize}
        gotoPage={gotoPage}
      />
    </div>
  )
}

export default PatientScans